import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight, Sparkles, Cpu, Bug, ShieldCheck, TestTube2, Rocket,
  Boxes, GitBranch, Eye, Code2, Layers,
} from "lucide-react";

const AGENTS = [
  { name: "Product", icon: Sparkles, color: "text-indigo-400", desc: "Turns your idea into clear requirements." },
  { name: "Architect", icon: Layers, color: "text-cyan-400", desc: "Designs files, data and structure." },
  { name: "Coding", icon: Code2, color: "text-emerald-400", desc: "Writes real, runnable source code." },
  { name: "Debugging", icon: Bug, color: "text-amber-400", desc: "Reads errors and patches them." },
  { name: "Testing", icon: TestTube2, color: "text-purple-400", desc: "Runs smoke tests on every build." },
  { name: "Security", icon: ShieldCheck, color: "text-rose-400", desc: "Scans code for unsafe patterns." },
  { name: "Deployment", icon: Rocket, color: "text-blue-400", desc: "Prepares production configuration." },
];

const FEATURES = [
  { icon: Cpu, title: "Multi-agent engine", desc: "A pipeline of specialized agents plans, builds, tests and secures your app — no single mega-prompt." },
  { icon: Eye, title: "Live sandboxed preview", desc: "Every build runs instantly in an isolated iframe with desktop, tablet and mobile viewports." },
  { icon: Code2, title: "Full browser IDE", desc: "Inspect and edit real generated files in a Monaco editor with tabs and diff view." },
  { icon: GitBranch, title: "Versioned history", desc: "Every change is snapshotted so you can revert to any previous build in one click." },
  { icon: Boxes, title: "Bring your own model", desc: "OpenAI, Anthropic, Gemini, OpenRouter, Ollama or a custom endpoint — configured by the owner." },
  { icon: ShieldCheck, title: "Secure by design", desc: "Encrypted secrets, RBAC, audit logs and sandboxed execution. No credits, ever." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [prompt, setPrompt] = useState("");

  const start = () => {
    if (user) navigate("/dashboard/new", { state: { prompt } });
    else navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100">
      <header className="sticky top-0 z-40 dc-glass border-b border-slate-800/70">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-400">
            <a href="#features" className="hover:text-emerald-400 transition-colors">Features</a>
            <a href="#agents" className="hover:text-emerald-400 transition-colors">Agents</a>
            <a href="#how" className="hover:text-emerald-400 transition-colors">How it works</a>
          </nav>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            {user ? (
              <button data-testid="nav-dashboard" onClick={() => navigate("/dashboard")}
                className="h-9 px-4 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 text-sm font-semibold">Dashboard</button>
            ) : (
              <>
                <button data-testid="nav-login" onClick={() => navigate("/login")} className="text-sm text-slate-300 hover:text-white px-3">Sign in</button>
                <button data-testid="nav-register" onClick={() => navigate("/login")}
                  className="h-9 px-4 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 text-sm font-semibold">Sign in</button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative dc-grid-bg overflow-hidden">
        <div className="absolute -top-40 left-1/3 w-[520px] h-[520px] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="max-w-5xl mx-auto px-5 pt-24 pb-20 text-center relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 text-xs text-emerald-300 mb-8 dc-fade-up">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 dc-pulse" /> Autonomous engineering team, on demand
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-head font-bold tracking-tight leading-none mb-6 dc-fade-up">
            Describe an app.<br /><span className="dc-gradient-text">Watch agents build it.</span>
          </h1>
          <p className="text-slate-400 text-base sm:text-lg max-w-2xl mx-auto mb-10 dc-fade-up">
            D Code plans, writes real code, tests, debugs and previews your application live — driven entirely by natural language.
          </p>

          <div className="max-w-2xl mx-auto dc-glass border border-slate-700/70 rounded-2xl p-3 flex flex-col sm:flex-row gap-2 dc-glow dc-fade-up">
            <textarea data-testid="hero-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)}
              placeholder="Build a landing page for a coffee shop with a menu and contact form..."
              rows={2}
              className="flex-1 resize-none bg-transparent px-3 py-2 text-sm focus:outline-none placeholder:text-slate-500" />
            <button data-testid="hero-start-btn" onClick={start}
              className="h-11 sm:self-end px-5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold flex items-center justify-center gap-2 whitespace-nowrap hover:opacity-90 transition-opacity">
              Start building <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-4">No credits. No limits meters. Configure your own models.</p>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-5 py-24">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-head font-semibold tracking-tight text-center mb-3">Everything an engineering team ships</h2>
        <p className="text-slate-400 text-center mb-14 max-w-2xl mx-auto">A complete development environment where the work is real — not simulated.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="group rounded-2xl border border-slate-800 bg-slate-900/40 p-6 hover:border-emerald-500/40 transition-colors">
              <div className="h-11 w-11 rounded-xl bg-slate-800/80 flex items-center justify-center mb-4 group-hover:bg-emerald-500/15 transition-colors">
                <f.icon className="h-5 w-5 text-emerald-400" />
              </div>
              <h3 className="font-head font-semibold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Agents */}
      <section id="agents" className="border-y border-slate-800/70 bg-slate-950/40">
        <div className="max-w-7xl mx-auto px-5 py-24">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-head font-semibold tracking-tight text-center mb-3">Seven specialized agents</h2>
          <p className="text-slate-400 text-center mb-14 max-w-2xl mx-auto">Each build is a real pipeline. Every step you see maps to an actual backend operation.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {AGENTS.map((a) => (
              <div key={a.name} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
                <a.icon className={`h-6 w-6 ${a.color} mb-3`} />
                <div className="font-head font-semibold mb-1">{a.name} Agent</div>
                <p className="text-xs text-slate-400 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How */}
      <section id="how" className="max-w-5xl mx-auto px-5 py-24">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-head font-semibold tracking-tight text-center mb-14">Prompt → Plan → Build → Preview</h2>
        <div className="grid sm:grid-cols-4 gap-4">
          {["Describe your app in plain language", "Agents plan and generate real files", "Code runs live in a sandboxed preview", "Refine, version and deploy"].map((s, i) => (
            <div key={i} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6 text-center">
              <div className="mx-auto h-10 w-10 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 text-slate-950 font-bold flex items-center justify-center mb-4">{i + 1}</div>
              <p className="text-sm text-slate-300">{s}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-14">
          <button data-testid="cta-start" onClick={start}
            className="h-12 px-8 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold inline-flex items-center gap-2 hover:opacity-90 transition-opacity">
            Build your first app <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-slate-800/70">
        <div className="max-w-7xl mx-auto px-5 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <Logo />
          <p>© 2026 D Code. An autonomous AI development platform.</p>
        </div>
      </footer>
    </div>
  );
}
